/**
 * Default module resolution for the mobile "Home" tab.
 *
 * There is no default-module field on the account (or anywhere on the server)
 * today, so "Home" is derived purely from the permission payload:
 *
 *   1. /dashboard when the user is permitted to open it;
 *   2. otherwise the first module the user is permitted to open (sidebar
 *      order) that is not already one of the other three tabs' destinations,
 *      so Home never duplicates a tab;
 *   3. otherwise the first permitted module at all;
 *   4. otherwise /dashboard — the route every unmatched URL already
 *      redirects to.
 *
 * The permission payload is the one DashboardLayout puts on ModulesContext and
 * publishes as "modulePermission": an array of sections shaped like
 *   { name: "Inventory", value: [{ name: "Items", value: true }, ...] }
 */

/** Where "Home" lands when nothing else can be resolved. */
export const FALLBACK_MODULE_PATH = "/dashboard";

/**
 * Section order used by the sidebar. Mirrors `menuOrder` in SidebarCore so the
 * "first permitted module" is the first thing the user would see listed.
 */
const SECTION_ORDER = [
  "Home",
  "Company",
  "Reservations",
  "Inventory",
  "Inventory Management",
  "Device Configurations",
  "Settings",
  "Administration",
  "Finance",
  "Report",
];

/**
 * Permission name -> route, mirroring `menuTemplate` in SidebarCore plus the
 * two entries the sidebar folds in from the backend's "Reservations" section.
 *
 * Every value here MUST be a route the app actually registers — App.tsx builds
 * its route table out of `HomeSidebar`/`ConfigurationSideBarInventory`/
 * `FiananceSideBar`/`ConfigurationSideBar`/`HistorySideBar`/`LedgerSideBar`/
 * `AdministraionSideBar`/`ReportSideBar` (finex-web + inventory-web
 * `src/ExampleData`), and anything else falls through to the `*` route, which
 * redirects to /dashboard. A permission whose destination is /dashboard when
 * the role has no Dashboard permission lands the user back on this very 403
 * page, so an unregistered path here is a loop, not a cosmetic slip.
 *
 * A name that is NOT listed has no known route: `resolveModulePath` returns
 * null for it and the resolver skips it, rather than guessing
 * `/{camelCasedName}` (the sidebar's guess, which is wrong for every
 * kebab-cased route — /reporting/sales-overview, /journal-entries,
 * /financial-reports, /payroll-payments, /pos-reports).
 */
const MODULE_PATHS: Record<string, Record<string, string>> = {
  Home: {
    Dashboard: "/dashboard",
  },
  Company: {
    "Company Details": "/companies",
    Locations: "/locations",
    "Floor Plans": "/floorPlan",
    "Sale Points": "/salePoint",
    "Reservations Setup": "/reservations",
    Employees: "/employees",
    Clients: "/clients",
  },
  Reservations: {
    "Reservation Setups": "/reservations",
    Reservations: "/tableReservations",
  },
  Inventory: {
    Items: "/items",
    Attributes: "/attributes",
    Inventory: "/inventory",
    "Product Modifiers": "/productModifiers",
  },
  "Inventory Management": {
    "Order Management": "/orderManagement",
    Transfers: "/transfer",
    Deliveries: "/deliveries",
    // The stock-count LIST is /stockCounts; /count only exists as /count/new
    // and /count/:id (inventory-web ConfigurationSideBar indices 38-40), so a
    // bare /count is not a registered route. Both spellings are mapped:
    // ProtectedRoute's COMPONENT_TO_PERMISSION_MAP says the permission is
    // "Stock Counts", SidebarCore's menuTemplate says "Stock Count".
    "Stock Count": "/stockCounts",
    "Stock Counts": "/stockCounts",
    "Count Approval": "/countsApproval",
    Corrections: "/corrections",
    "Order Templates": "/orderTemplates",
    "Order Approval": "/orderApproval",
    "Supplier Inventory": "/supplierInventory",
    "Supplier Return": "/supplierReturn",
  },
  "Device Configurations": {
    Devices: "/devices",
    "Device Users": "/deviceUser",
    "Device Tender": "/deviceTender",
    "Device Health": "/deviceHealth",
    "App Version": "/appVersion",
  },
  Settings: {
    Reasons: "/reasons",
    Schedules: "/schedules",
    Promotion: "/promotion",
  },
  Administration: {
    Tax: "/tax",
    "Payment Terms": "/terms",
    Roles: "/roles",
    Languages: "/languages",
  },
  Finance: {
    Purchases: "/purchases",
    Sales: "/sales",
    Assets: "/assets",
    "Purchase Ledger": "/purchaseLedger",
    "Sales Ledger": "/salesLedger",
    "Transaction Month": "/transactionMonth",
    // Registered by finex-web LedgerSideBar / ReportSideBar. Their routes are
    // kebab-cased, so the old camelCase guess (/journalEntries,
    // /payrollPayments) matched nothing.
    "Journal Entries": "/journal-entries",
    "Payroll Payments": "/payroll-payments",
    "Financial Reports": "/financialReports",
  },
  Report: {
    "General Report": "/generalReport",
    "Item Movement": "/itemMovement",
    "Recent Sales": "/recentSales",
    // SidebarCore routes this to /reporting/sales-overview (inventory-web
    // ConfigurationSideBar index 45). Camel-casing it gave /salesOverview.
    "Sales Overview": "/reporting/sales-overview",
    "Product Performance": "/productPerformance",
    "POS Reports": "/pos-reports",
    "Table Reservations": "/tableReservations",
    "Customer Orders": "/customerOrders",
    "Financial Reports": "/financialReports",
  },
};

/**
 * Same table, flattened. The backend owns the section names, and it has moved
 * permissions between sections before (the "Reservations" section the sidebar
 * folds into two other groups); a name we know the route for should not become
 * unresolvable just because it arrived under an unexpected heading.
 */
const FLAT_MODULE_PATHS: Record<string, string> = Object.values(
  MODULE_PATHS
).reduce((flat: Record<string, string>, section) => {
  Object.entries(section).forEach(([moduleName, path]) => {
    if (!(moduleName in flat)) flat[moduleName] = path;
  });
  return flat;
}, {});

/**
 * Route for a permission entry, or null when no registered route is known for
 * it. Null means "skip me" — never guess, because a guessed path that is not
 * registered falls through App.tsx's `*` route to /dashboard, i.e. straight
 * back to 403 for a role without Dashboard.
 */
export const resolveModulePath = (
  sectionName: string,
  moduleName: string
): string | null =>
  MODULE_PATHS[sectionName]?.[moduleName] ??
  FLAT_MODULE_PATHS[moduleName] ??
  null;

/** True when the permission payload grants `moduleName`. */
export const isModulePermitted = (
  modules: any[],
  moduleName: string
): boolean =>
  (modules || []).some((section: any) =>
    (section?.value || []).some(
      (item: any) => item?.name === moduleName && item?.value === true
    )
  );

/** Every route the permission payload grants, in sidebar order. */
const permittedModulePaths = (modules: any[]): string[] => {
  const sections = modules || [];
  const ordered = [
    ...SECTION_ORDER.map((name: string) =>
      sections.find((section: any) => section?.name === name)
    ).filter(Boolean),
    ...sections.filter(
      (section: any) => !SECTION_ORDER.includes(section?.name)
    ),
  ];

  return ordered.flatMap((section: any) =>
    (section?.value || [])
      .filter((item: any) => item?.value === true)
      // Dynamic sections (the report groups DashboardLayout appends) carry
      // their own path — always /report/{id}, a registered route; the static
      // ones are looked up in the table above.
      .map((item: any) => item?.path || resolveModulePath(section?.name, item?.name))
      // Permissions with no known route are skipped, not guessed at.
      .filter((path: string | null): path is string => Boolean(path))
  );
};

/**
 * Routes the other three tabs already own. Home skips these so that it never
 * duplicates a tab; keep in step with the `tabs` list in BottomTabBar.
 */
const OTHER_TAB_PATHS = ["/items", "/productModifiers", "/reporting/sales-overview"];

/**
 * Where the "Home" tab navigates to: /dashboard when permitted, else the first
 * permitted module that is not another tab, else the first permitted module,
 * else /dashboard.
 */
export const resolveDefaultModulePath = (modules: any[]): string => {
  const permitted = permittedModulePaths(modules);

  if (permitted.includes(FALLBACK_MODULE_PATH)) {
    return FALLBACK_MODULE_PATH;
  }

  return (
    permitted.find((path) => !OTHER_TAB_PATHS.includes(path)) ??
    permitted[0] ??
    FALLBACK_MODULE_PATH
  );
};
